<script setup lang="ts">
import { Delete, Edit, Plus } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { computed, onMounted, ref } from "vue";
import type {
  TreeDefinitionDto,
  TreeDefinitionSummaryDto,
  TreeLevelDto,
} from "~/api";
import TreeNodeManager from "~/Compliance/components/TreeNodeManager.vue";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { useTreeDefinitionStore } from "~/stores/TreeDefinitionStore";
import { apiClient } from "~/utils/apiClient";
import { drawerWidth } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

const myInfoStore = useMyInfoStore();
const treeDefinitionStore = useTreeDefinitionStore();

const loading = ref(true);
const drawerOpen = ref(true);
const saving = ref(false);
const editing = ref(false);

const drawerTitle = computed(() => {
  if (isNew.value) return "New Tree Definition";
  return treeDef.value.name;
});

const props = defineProps<{
  treeDefinitionSummary: TreeDefinitionSummaryDto;
}>();

const emit = defineEmits<{
  (e: "closeDrawer"): void;
  (e: "added"): void;
  (e: "updated"): void;
  (e: "deleted"): void;
}>();

const isNew = ref(false);
const treeDef = ref<TreeDefinitionDto>({
  guid: "",
  name: "",
  description: "",
  treeType: undefined,
  levels: [],
  nodes: [],
});

// Temporary state for new levels being added
const newLevelName = ref("");

onMounted(async () => {
  const guidWanted = props.treeDefinitionSummary.guid;

  if (guidWanted?.startsWith("new") || !guidWanted) {
    isNew.value = true;
    loading.value = false;
    return;
  }

  try {
    await apiClient.tree.getTreeDefinition(guidWanted).then((def) => {
      treeDef.value = def;
    });
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
});

const save = async () => {
  if (!treeDef.value.name.trim()) {
    ElMessage.warning("Name is required");
    return;
  }

  saving.value = true;
  try {
    if (isNew.value) {
      const result = await apiClient.tree.createTreeDefinition(
        myInfoStore.currentSysOrgGuid,
        {
          name: treeDef.value.name,
          description: treeDef.value.description,
          treeType: treeDef.value.treeType || undefined,
          levels: treeDef.value.levels.map((l) => ({
            name: l.name,
            depth: l.depth,
          })),
        },
      );
      treeDef.value = result;
      isNew.value = false;
      editing.value = false;
      treeDefinitionStore.clear();
      ElMessage.success("Tree definition created");
      emit("added");
    } else {
      const result = await apiClient.tree.updateTreeDefinition(
        treeDef.value.guid,
        {
          name: treeDef.value.name,
          description: treeDef.value.description,
        },
      );
      treeDef.value = {
        ...treeDef.value,
        name: result.name,
        description: result.description,
      };
      editing.value = false;
      treeDefinitionStore.clear();
      ElMessage.success("Tree definition updated");
      emit("updated");
    }
  } catch (error) {
    showError(error);
  } finally {
    saving.value = false;
  }
};

const deleteTreeDefinition = async () => {
  try {
    await ElMessageBox.confirm(
      "This will permanently delete this tree definition and all its nodes. Continue?",
      "Warning",
      {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning",
      },
    );
    await apiClient.tree.deleteTreeDefinition(treeDef.value.guid);
    treeDefinitionStore.clear();
    ElMessage.success("Tree definition deleted");
    emit("deleted");
    drawerOpen.value = false;
    emit("closeDrawer");
  } catch (error) {
    if (error !== "cancel") {
      showError(error);
    }
  }
};

const addLevel = async () => {
  if (!newLevelName.value.trim()) {
    ElMessage.warning("Level name is required");
    return;
  }

  const nextDepth =
    treeDef.value.levels.length > 0
      ? Math.max(...treeDef.value.levels.map((l) => l.depth)) + 1
      : 0;

  if (isNew.value) {
    // For new definitions, just add to the local array
    treeDef.value.levels.push({
      guid: "temp-" + Date.now(),
      name: newLevelName.value.trim(),
      depth: nextDepth,
    });
    newLevelName.value = "";
    return;
  }

  try {
    const level = await apiClient.tree.createTreeLevel(treeDef.value.guid, {
      name: newLevelName.value.trim(),
      depth: nextDepth,
    });
    treeDef.value.levels.push(level);
    newLevelName.value = "";
    ElMessage.success("Level added");
  } catch (error) {
    showError(error);
  }
};

const updateLevelName = async (level: TreeLevelDto) => {
  if (isNew.value) return;

  try {
    await apiClient.tree.updateTreeLevel(level.guid, { name: level.name });
    ElMessage.success("Level name updated");
  } catch (error) {
    showError(error);
  }
};

const removeLevel = async (level: TreeLevelDto, index: number) => {
  if (isNew.value) {
    treeDef.value.levels.splice(index, 1);
    // Reorder depths
    treeDef.value.levels.forEach((l, i) => (l.depth = i));
    return;
  }

  try {
    await apiClient.tree.deleteTreeLevel(level.guid);
    treeDef.value.levels.splice(index, 1);
    ElMessage.success("Level removed");
  } catch (error) {
    showError(error);
  }
};

const handleClose = () => {
  drawerOpen.value = false;
  emit("closeDrawer");
};

const refreshTree = async () => {
  if (isNew.value || !treeDef.value.guid) return;
  loading.value = true;
  try {
    treeDef.value = await apiClient.tree.getTreeDefinition(treeDef.value.guid);
  } catch (error) {
    showError(error);
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <el-drawer
    v-model="drawerOpen"
    :size="drawerWidth"
    :title="drawerTitle"
    @close="handleClose"
  >
    <template #header>
      <div class="drawer-header">
        <span class="el-drawer__title">{{ drawerTitle }}</span>
      </div>
    </template>
    <div v-loading="loading">
      <div class="description">
        <template v-if="!isNew && !loading">
          <el-tag v-if="treeDef.treeType">
            {{ treeDef.treeType }}
          </el-tag>
          <span v-if="treeDef.description" class="drawer-description">
            {{ treeDef.description }}
          </span>
        </template>
        <el-button
          v-if="!isNew && !editing && !loading"
          :icon="Edit"
          text
          @click="editing = true"
        />
      </div>
      <el-form v-if="!loading" label-position="top" class="compact-form">
        <!-- Editable Info (new items always show form; existing items show on edit click) -->
        <template v-if="isNew || editing">
          <el-form-item label="Name">
            <el-input v-model="treeDef.name" placeholder="e.g., Geo Settings" />
          </el-form-item>

          <el-row v-if="isNew" :gutter="12">
            <el-col :span="12">
              <el-form-item label="Type">
                <el-select
                  v-model="treeDef.treeType"
                  placeholder="General (default)"
                  clearable
                  style="width: 100%"
                >
                  <el-option label="Geo Tree" value="GeoTree" />
                  <el-option label="Sector Setup" value="SectorSetup" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item label="Description" style="margin-bottom: 12px">
            <el-input
              v-model="treeDef.description"
              type="textarea"
              :rows="1"
              :autosize="{ minRows: 1, maxRows: 3 }"
              placeholder="Optional description"
            />
          </el-form-item>

          <el-form-item style="margin-bottom: 8px">
            <el-button type="primary" :loading="saving" @click="save">
              {{ isNew ? "Create" : "Save" }}
            </el-button>
            <el-button v-if="!isNew" @click="editing = false">
              Cancel
            </el-button>
            <el-button
              v-if="!isNew && myInfoStore.hasSetupBasicRole"
              type="danger"
              :icon="Delete"
              @click="deleteTreeDefinition"
            >
              Delete
            </el-button>
          </el-form-item>
        </template>

        <!-- Levels -->
        <el-divider style="margin: 12px 0" />
        <div
          style="
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            margin-bottom: 8px;
          "
        >
          <h3 style="margin: 0">Hierarchy Levels</h3>
          <span style="color: var(--el-text-color-secondary); font-size: 12px">
            Top (level 0) → Bottom
          </span>
        </div>

        <el-table
          :data="treeDef.levels"
          style="width: 100%; margin-bottom: 8px"
        >
          <el-table-column label="Level" prop="depth" width="80" />
          <el-table-column label="Level Name" width="190">
            <template #default="{ row }">
              <el-input
                v-model="row.name"
                @blur="updateLevelName(row)"
                @keyup.enter="updateLevelName(row)"
              />
            </template>
          </el-table-column>
          <el-table-column label="" width="60">
            <template #default="{ row, $index }">
              <el-button
                text
                size="small"
                type="danger"
                :icon="Delete"
                title="Remove level"
                @click="removeLevel(row, $index)"
              />
            </template>
          </el-table-column>
        </el-table>

        <div style="display: flex; gap: 8px; width: 300px">
          <el-input
            v-model="newLevelName"
            placeholder="New level name"
            @keyup.enter="addLevel"
          />
          <el-button type="primary" :icon="Plus" @click="addLevel">
            Add
          </el-button>
        </div>

        <!-- Nodes (only for existing definitions) -->
        <template v-if="!isNew && treeDef.levels.length > 0">
          <el-divider />
          <h3>Trees</h3>
          <TreeNodeManager :tree-definition="treeDef" @refresh="refreshTree" />
        </template>
      </el-form>
    </div>
  </el-drawer>
</template>

<style scoped>
.drawer-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  min-width: 0;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-description {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 30px;
}

.compact-form :deep(.el-form-item) {
  margin-bottom: 12px;
}

.compact-form :deep(.el-form-item__label) {
  padding-bottom: 4px;
}
</style>
